
import React, { useState, useEffect } from 'react';
import Logo from './Logo';
import ContactForm from './ContactForm';
import HeroVisual from './HeroVisual';
import CookieConsentBanner from './CookieConsentBanner';
import { readCookieConsent } from './cookieConsentStorage';
import { loadYandexMetrika } from './yandexMetrika';
import { PRODUCTS, PILOTS, COMPANY, NAV } from './data/siteData';

const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const App = () => {
    const [formOpen, setFormOpen] = useState(false);
    const [formProduct, setFormProduct] = useState('betuple');
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        document.body.style.overflow = formOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [formOpen]);

    useEffect(() => {
        const consent = readCookieConsent();
        if (consent?.analytics) {
            loadYandexMetrika();
        }
    }, []);

    const openForm = (product = 'betuple') => {
        setFormProduct(product);
        setFormOpen(true);
    };

    const closeForm = () => {
        setFormOpen(false);
        setFormProduct('betuple');
    };

    const handleNav = (id) => {
        setMenuOpen(false);
        if (id === 'contact') {
            openForm('betuple');
            return;
        }
        scrollTo(id);
    };

    return (
        <>
            {formOpen && (
                <ContactForm onClose={closeForm} initialProduct={formProduct} />
            )}

            <CookieConsentBanner />

            <div className="page">
                <header className="site-header">
                    <Logo onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} />
                    <nav className="site-nav" aria-label="Основная навигация">
                        {NAV.map((item) => (
                            <a
                                key={item.id}
                                href={`#${item.id}`}
                                className="site-nav__link"
                                onClick={(event) => {
                                    event.preventDefault();
                                    handleNav(item.id);
                                }}
                            >
                                {item.label}
                            </a>
                        ))}
                    </nav>
                    <button
                        type="button"
                        className="button button--primary button--header"
                        onClick={() => openForm()}
                    >
                        Запросить пилот
                    </button>
                    <button
                        type="button"
                        className={`burger ${menuOpen ? 'burger--open' : ''}`}
                        aria-label="Меню"
                        aria-expanded={menuOpen}
                        onClick={() => setMenuOpen(!menuOpen)}
                    >
                        <span />
                        <span />
                    </button>
                </header>

                {menuOpen && (
                    <nav className="mobile-nav" aria-label="Мобильное меню">
                        {NAV.map((item) => (
                            <a
                                key={item.id}
                                href={`#${item.id}`}
                                className="mobile-nav__link"
                                onClick={(event) => {
                                    event.preventDefault();
                                    handleNav(item.id);
                                }}
                            >
                                {item.label}
                            </a>
                        ))}
                    </nav>
                )}

                <main>
                    <section className="hero">
                        <div className="hero__content">
                            <p className="eyebrow">ООО «Битапл» · betuple.com</p>
                            <h1 className="hero__title">
                                Продуктовая инженерия для&nbsp;B2B
                            </h1>
                            <p className="hero__subtitle">
                                Продуктовая компания: собственные B2B-решения для контроля
                                маржи в аутсорсе, безопасной поставки контейнеров и кодинг-ассистента
                                с квотами для команд.
                            </p>
                            <div className="hero__actions">
                                <button
                                    type="button"
                                    className="button button--primary"
                                    onClick={() => scrollTo('products')}
                                >
                                    Смотреть продукты
                                </button>
                                <button
                                    type="button"
                                    className="button button--ghost"
                                    onClick={() => openForm()}
                                >
                                    Запросить пилот
                                </button>
                            </div>
                        </div>
                        <HeroVisual />
                    </section>

                    <section id="products" className="section products">
                        <div className="section__head">
                            <p className="eyebrow">Продукты</p>
                            <h2 className="section__title">Решения, которые мы развиваем</h2>
                            <p className="section__lead">
                                Три направления с общим ДНК: практический опыт delivery-команд
                                и измеримая ценность для бизнеса.
                            </p>
                        </div>
                        <div className="products__grid">
                            {PRODUCTS.map((product) => (
                                <article
                                    key={product.id}
                                    className={`product-card product-card--${product.accent}`}
                                >
                                    <div className="product-card__badge">{product.name}</div>
                                    <h3 className="product-card__tagline">{product.tagline}</h3>
                                    <p className="product-card__description">{product.description}</p>
                                    <ul className="product-card__features">
                                        {product.features.map((feature) => (
                                            <li key={feature}>{feature}</li>
                                        ))}
                                    </ul>
                                    <p className="product-card__audience">
                                        <strong>Для кого:</strong> {product.audience}
                                    </p>
                                    <a
                                        className="button button--product"
                                        href={product.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {product.cta} →
                                    </a>
                                </article>
                            ))}
                        </div>
                    </section>

                    <section id="pilots" className="section pilots-section">
                        <div className="section__head">
                            <p className="eyebrow">Пилоты</p>
                            <h2 className="section__title">Внедрение и пилоты</h2>
                            <p className="section__lead">
                                Помогаем запустить продукт в вашем контуре: от согласования сценария
                                до первых измеримых результатов.
                            </p>
                        </div>
                        <ul className="pilots-grid">
                            {PILOTS.map((pilot) => (
                                <li
                                    key={pilot.id}
                                    className={`pilot-card pilot-card--${pilot.accent}`}
                                >
                                    <span className="pilot-card__product">{pilot.product}</span>
                                    <h3 className="pilot-card__title">{pilot.title}</h3>
                                    <p className="pilot-card__text">{pilot.text}</p>
                                    <button
                                        type="button"
                                        className="button button--ghost pilot-card__cta"
                                        onClick={() => openForm(pilot.productId || pilot.id)}
                                    >
                                        Запросить пилот
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </section>

                    <section id="about" className="section about">
                        <div className="about__inner">
                            <div className="about__text">
                                <p className="eyebrow">О компании</p>
                                <h2 className="section__title">Betuple</h2>
                                <p>
                                    {COMPANY.name} — российская продуктовая компания.
                                    Развиваем MarginLayer, DevOpsFort и bc и сопровождаем клиентов
                                    на этапе пилота и внедрения.
                                </p>
                                <p>
                                    Команда совмещает опыт delivery, архитектуры и DevOps —
                                    продукты создаём и развиваем сами, без заказной разработки
                                    под ключ.
                                </p>
                            </div>
                            <div className="about__facts">
                                <div className="fact-card">
                                    <span className="fact-card__value">3</span>
                                    <span className="fact-card__label">собственных продукта</span>
                                </div>
                                <div className="fact-card">
                                    <span className="fact-card__value">B2B</span>
                                    <span className="fact-card__label">фокус на бизнес-клиентах</span>
                                </div>
                                <div className="fact-card">
                                    <span className="fact-card__value">RU</span>
                                    <span className="fact-card__label">продукты и пилоты в России</span>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section id="contact" className="section cta">
                        <div className="cta__inner">
                            <h2 className="cta__title">Запросить пилот</h2>
                            <p className="cta__text">
                                MarginLayer, DevOpsFort или bc — напишите, и мы вернёмся с ответом
                                в течение 24 часов в рабочие дни. Тарифы bc:{' '}
                                <a href="https://bc.apps.betuple.com/landing" target="_blank" rel="noopener noreferrer">
                                    bc.apps.betuple.com/landing
                                </a>
                                .
                            </p>
                            <button
                                type="button"
                                className="button button--primary button--large"
                                onClick={() => openForm()}
                            >
                                Оставить заявку
                            </button>
                        </div>
                    </section>
                </main>

                <footer className="site-footer">
                    <div className="site-footer__brand">
                        <Logo />
                        <p className="site-footer__legal">
                            {COMPANY.name}
                            <br />
                            {COMPANY.address}
                        </p>
                    </div>
                    <div className="site-footer__links">
                        <a href="/devops/">DevOps-услуги</a>
                        <a href="https://marginlayer.app/ru" target="_blank" rel="noopener noreferrer">
                            MarginLayer
                        </a>
                        <a href="https://devopsfort.ru/" target="_blank" rel="noopener noreferrer">
                            DevOpsFort
                        </a>
                        <a href="https://bc.apps.betuple.com/landing" target="_blank" rel="noopener noreferrer">
                            bc
                        </a>
                        <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
                        <a href="/conf-policy/">Политика конфиденциальности</a>
                        <a href="/personal-data/">Обработка персональных данных</a>
                    </div>
                </footer>
            </div>
        </>
    );
};

export default App;
